import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonDirective } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Ripple } from 'primeng/ripple';
import { Property } from '../../domain/property';
import { PmsService } from '../../services/pms.service';
import { DomainService } from '../../services/domain.service';
import { PropertyStoreService } from '../../services/stores/property-store.service';
import { PropertyService } from '../../services/property.service';
import { PropertyPmsResponse } from '../../services/response/property-pms-response';
import { CheckboxModule } from 'primeng/checkbox';
import { AddPropertyToDomainRequest } from '../../services/request/add-property-to-domain-request';
import { Organisation } from '../../domain/organisation';
import { User } from '../../domain/user';
import { UserStoreService } from '../../services/stores/user-store.service';
import { PhobosTaskTemplate } from '../../domain/phobos-task-template';
import { AccordionModule } from 'primeng/accordion';
import { NgForOf } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputSwitchModule } from 'primeng/inputswitch';
import { WorkflowService } from '../../services/workflow.service';
import { RunRequest } from '../../services/request/run-request';
import { DateTime } from 'luxon';
import { RefreshDataState } from '../../domain/refresh-data-state';
import { DataStoreService } from '../../services/data-store.service';
import { firstValueFrom } from 'rxjs';

@Component({
   selector: 'app-org-wizard-dialog',
   standalone: true,
   imports: [
      AutoCompleteModule,
      ButtonDirective,
      CalendarModule,
      DialogModule,
      InputTextareaModule,
      ReactiveFormsModule,
      Ripple,
      CheckboxModule,
      FormsModule,
      AccordionModule,
      NgForOf,
      ProgressSpinnerModule,
      InputSwitchModule
   ],
   templateUrl: './org-wizard-dialog.component.html',
   styleUrl: './org-wizard-dialog.component.scss'
})
export class OrgWizardDialogComponent {
   @Input()
   isVisible: boolean = false;
   useTyallsTaskModel: boolean = false;
   useAi: boolean = false;
   selectedProperty: Property | undefined;
   currentUser: User | undefined;
   propertyPms: PropertyPmsResponse | undefined;
   selectedOrg: Organisation | undefined;
   orgTaskTemplates: PhobosTaskTemplate[] | undefined;

   isPreConfiguredLoading = false;
   isCustomSetupLoading = false;

   @Output() orgWizardDialogCloseClose = new EventEmitter();

   constructor(
      private pmsService: PmsService,
      private propertyStore: PropertyStoreService,
      private organisationService: DomainService,
      private propertyService: PropertyService,
      private userStore: UserStoreService,
      private _workflowService: WorkflowService,
      private dataStoreService: DataStoreService,
      private router: Router
   ) {
      this.userStore.currentUser.subscribe({
         next: (user) => {
            this.currentUser = user;
         },
         error: (e) => {
            console.error(e);
         }
      });
      this.propertyStore.currentProperty.subscribe((value) => {
         this.selectedProperty = value;
         this.propertyPms = this.selectedProperty.propertyPmsResponse;
         if (this.selectedProperty.organisations.length > 0) {
            this.selectedOrg = this.selectedProperty.organisations[0];
            this.orgTaskTemplates = this.selectedOrg.taskTemplates;
         }
      });
   }

   close() {
      this.isCustomSetupLoading = true;
      this.updateProperty(true);
   }

   closeDialog() {
      this.isVisible = false;
      this.orgWizardDialogCloseClose.emit();
   }

   async updateProperty(closeDialog: boolean = false) {
      if (this.selectedProperty) {
         const newProperty = await firstValueFrom(
            this.propertyService.updateProperty(this.selectedProperty?.id, true)
         );
         if (newProperty) {
            this.isPreConfiguredLoading = false;
            this.isCustomSetupLoading = false;
            this.propertyStore.setCurrentProperty(newProperty);
            if (closeDialog) {
               this.closeDialog();
            }
         }

         await this.router.navigate(['/property/admin/tyalls-configuration'], {
            queryParams: { tab: 'tasks' }
         });
      }
   }

   onOk() {
      if (this.selectedProperty) {
         this.isPreConfiguredLoading = true;
         if (this.propertyPms) {
            this.pmsService
               .addPropertyToPms(this.selectedProperty.id, this.propertyPms.pmsId)
               .subscribe({
                  next: async () => await this.addTyallsModelsToProperty(),
                  error: (e) => console.error(e),
                  complete: () => console.info('complete')
               });
         }
      }
   }

   async addTyallsModelsToProperty() {
      if (this.selectedProperty && this.selectedOrg) {
         const request = new AddPropertyToDomainRequest();
         request.addAllTaskTemplates = true;
         request.addAutoRunSchedules = true;
         request.copyAiConfig = this.useAi;
         const addToOrgResult = await firstValueFrom(
            this.organisationService.addPropertyToDomain(
               this.selectedOrg.id,
               this.selectedProperty.id,
               request
            )
         );
         if (this.selectedProperty) {
            for (const taskTemplate of addToOrgResult.taskTemplate) {
               console.log(`Running Task ${taskTemplate.id}`);
               const workflowRequest = new RunRequest();
               workflowRequest.workflowId = taskTemplate.id;
               workflowRequest.propertyId = this.selectedProperty.id;
               const d = DateTime.now();
               workflowRequest.runDate = d.toISODate();
               const result = await firstValueFrom(
                  this._workflowService.runWorkflow(workflowRequest)
               );
               console.log(
                  `Finished running Task ${taskTemplate.id} result ${JSON.stringify(result)}`
               );
            }
            await this.updateProperty(true);
            const refreshDate = new RefreshDataState();
            refreshDate.UnclaimedTasks = true;
            refreshDate.AllTasksMonthly = true;
            refreshDate.AllPropertyTasks = true;
            refreshDate.WorkflowHistory = true;
            this.dataStoreService.refreshTaskData(refreshDate);
            console.info('complete');
         }
      }
   }
}
